import { AuditActionSchema, type AuditAction } from "@starsach/shared-types";

/**
 * Every audit action as a sentence a person reads, for the Audit tab and
 * Account > Security. The code stays available as a title for anyone
 * matching against the API. `audit-labels.test.ts` fails the build when
 * an action is added to the vocabulary without a label here.
 */
const LABELS: Record<AuditAction, string> = {
  "environment.onboarded": "Environment onboarded",
  "environment.offboarded": "Environment offboarded",
  "environment.api_key_revoked": "API key revoked",
  "environment.api_key_rotated": "API key rotated",
  "environment.customer_profile_deleted": "Customer profile deleted",
  "environment.data_purged": "Environment data deleted",
  "environment.handover_letter_written": "Handover letter written",
  "environment.data_exported": "Environment data exported",
  "environment.export_waived": "Export declined",
  "environment.export_link_issued": "Export download link issued",
  "environment.export_downloaded": "Export downloaded",
  "environment.status_changed": "Environment status changed",
  "environment.live_changed": "Environment marked live or not live",
  "environment.approval_requirement_changed": "Approval requirement changed",
  "organisation.created": "Organization created",
  "organisation.chain_reordered": "Environment chain reordered",
  "organisation.renamed": "Organization renamed",
  "membership.granted": "Role granted",
  "membership.role_changed": "Role changed",
  "membership.revoked": "Role revoked",
  "membership.profiles_changed": "Visible profiles changed",
  "role.created": "Role created",
  "role.updated": "Role changed",
  "role.deleted": "Role deleted",
  "group.created": "Group created",
  "group.updated": "Group changed",
  "group.deleted": "Group deleted",
  "staff.role_changed": "Staff role changed",
  "staff.exclusion_changed": "Staff exclusion changed",
  "access.denied": "Refused - permission not held",
  "profile.created": "Configuration profile created",
  "profile.published": "Configuration published",
  "binding.created": "Number bound to a profile",
  "binding.removed": "Number unbound",
  "binding.requested": "Alias change requested",
  "binding.rejected": "Alias change rejected",
  "reference_alias.set": "Reference alias set",
  "reference_alias.removed": "Reference alias removed",
  "promotion.requested": "Promotion requested",
  "promotion.applied": "Promotion applied",
  "promotion.rejected": "Promotion rejected",
  "override.set": "Override set",
  "override.cleared": "Override cleared",
  "rollback.applied": "Rolled back",
  "quota_increase.requested": "Quota increase requested",
  "journey.inspected": "Call inspected",
  "journey.searched": "Calls searched",
  "rewind.agents_viewed": "Agents at a minute viewed",
  "support_cases.checked": "Support cases checked with AWS",
  "portal_user.created": "User created",
  "portal_user.invited": "User invited",
  "portal_user.disabled": "User disabled",
  "portal_user.enabled": "User re-enabled",
  "portal_user.removed": "User removed",
  "portal_user.left": "User left the organization",
  "portal_user.deleted": "User account deleted",
  "portal_user.authenticator_reset": "Authenticator reset",
  "portal_user.invitation_resent": "Invitation sent again",
  "sign_in_help.requested": "Asked for help signing in",
  "user.preferences_changed": "Preferences changed",
  "email.sent": "Email sent",
  "user.profile_changed": "Name changed",
  "user.email_change_requested": "Email change requested",
  "user.email_change_canceled": "Email change canceled",
  "user.email_changed": "Email changed",
  "user.email_change_reverted": "Email change undone by the old address",
  "user.signed_out_everywhere": "Signed out everywhere",
  "security.reported": "Sign-in reported as not mine",
  "identity_provider.registered": "Identity provider registered",
};

export function actionLabel(action: string): string {
  const parsed = AuditActionSchema.safeParse(action);
  return parsed.success ? LABELS[parsed.data] : action;
}

/** How an action ended, as a pill: applied is fine, rejected is a decision, denied is a refusal. */
export function resultTone(result: string): "ok" | "warning" | "critical" | "neutral" {
  return result === "applied" ? "ok" : result === "rejected" ? "warning" : result === "denied" || result === "failed" ? "critical" : "neutral";
}
